/**
 * Persistent location-tracking hook for StreakFree.
 *
 * - Persists the user's "share my location" preference in localStorage so that
 *   when they close & reopen the PWA, tracking resumes automatically without
 *   re-prompting them every time. Browser keeps the geolocation permission for
 *   the origin once granted, so as long as the user opted in once, this hook
 *   re-attaches `navigator.geolocation.watchPosition` on every app mount.
 *
 * - POSTs each position to `POST /api/team/location` which:
 *     a) upserts the worker's current pin so the Owner can see them on
 *        Worker Locations / Day Pilot
 *     b) runs server-side geofence detection against today's assigned jobs,
 *        recording arrived_at / departed_at + time-on-site
 *
 * Exposes:
 *   - `tracking` boolean (driven by localStorage + permission state)
 *   - `position` last known {lat, lng, accuracy}
 *   - `enable()` / `disable()` to flip the preference
 *   - `permissionDenied` so the UI can show a "go to settings" prompt
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const STORAGE_KEY = 'streakfree_tracking_enabled';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function useLocationTracking({ enabled: explicitlyEnabled } = {}) {
  // Hydrate from localStorage on mount — this is the source of truth for
  // "user opted in" across PWA close/reopen cycles. Default OFF.
  const [tracking, setTracking] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [position, setPosition] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const watchIdRef = useRef(null);
  const lastSentAtRef = useRef(0);

  const sendLocation = useCallback(async (pos) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      if (!token) return;
      // Rate-limit POSTs to once every ~15s even if watchPosition fires more
      // often, to keep server load + battery sane.
      const now = Date.now();
      if (now - lastSentAtRef.current < 15000) return;
      lastSentAtRef.current = now;

      await fetch(`${API_URL}/api/team/location`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          heading: pos.coords.heading ?? null,
          speed: pos.coords.speed ?? null,
        }),
      });
    } catch (e) {
      // Silent — offline / network hiccup. watchPosition will keep firing.
    }
  }, []);

  const startWatch = useCallback(() => {
    if (!navigator?.geolocation) return;
    if (watchIdRef.current !== null) return;  // already watching

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setPosition({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
        setPermissionDenied(false);
        sendLocation(pos);
      },
      (err) => {
        if (err.code === 1) {
          setPermissionDenied(true);
          // Don't auto-disable — keep `tracking` true so when the user grants
          // permission later we resume seamlessly.
        }
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 }
    );
  }, [sendLocation]);

  const stopWatch = useCallback(() => {
    if (watchIdRef.current !== null && navigator?.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  const enable = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, 'true'); } catch (_) {}
    setTracking(true);
  }, []);

  const disable = useCallback(async () => {
    try { localStorage.setItem(STORAGE_KEY, 'false'); } catch (_) {}
    setTracking(false);
    stopWatch();
    // Tell the server to clear our pin so the Owner doesn't keep seeing a
    // stale "last seen at 09:42" indicator after we've gone off-shift.
    try {
      const token = localStorage.getItem('streakfree_token');
      if (token) {
        await fetch(`${API_URL}/api/team/location`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });
      }
    } catch (_) {}
  }, [stopWatch]);

  // Master effect — starts/stops watch based on `tracking` + explicit override.
  useEffect(() => {
    const shouldTrack = explicitlyEnabled ?? tracking;
    if (shouldTrack) {
      startWatch();
    } else {
      stopWatch();
    }
    return stopWatch;
  }, [tracking, explicitlyEnabled, startWatch, stopWatch]);

  return { tracking, position, permissionDenied, enable, disable };
}

export default useLocationTracking;
